import { getApiBase, getAuthHeaders } from '../config/api';
import { apiClient } from './apiClient';

/**
 * Định nghĩa cấu trúc dữ liệu giao dịch
 */
export type TransactionDto = {
  /** ID duy nhất của giao dịch */
  id: number;
  /** Mã giao dịch */
  transactionCode: string;
  /** Loại giao dịch */
  type: 'deposit' | 'withdraw' | 'transfer' | 'payment' | 'refund' | 'commission';
  /** Trạng thái giao dịch */
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  /** Số tiền giao dịch */
  amount: number;
  /** Mô tả giao dịch (tùy chọn) */
  description?: string;
  /** Thời gian tạo giao dịch */
  createdAt: string;
  /** ID người dùng thực hiện giao dịch */
  userId: number;
  /** Thông tin người dùng */
  user?: {
    id: number;
    email: string;
    fullName: string;
  };
};

/**
 * Service quản lý các giao dịch
 */
export class TransactionsService {
  private static baseUrl = getApiBase();

  /**
   * Lấy headers xác thực cho các request API
   * @returns Object chứa headers xác thực
   */
  static getAuthHeaders(): Record<string, string> {
    return getAuthHeaders();
  }

  /**
   * Lấy danh sách tất cả giao dịch (dành cho admin)
   * @param params - Tham số lọc và phân trang
   * @param params.page - Số trang
   * @param params.limit - Số lượng giao dịch mỗi trang
   * @param params.status - Lọc theo trạng thái
   * @param params.type - Lọc theo loại giao dịch
   * @param params.fromDate - Lọc từ ngày
   * @param params.toDate - Lọc đến ngày
   * @returns Promise chứa danh sách giao dịch và thông tin phân trang
   */
  static async listAll(params: { page?: number; limit?: number; status?: string; type?: string; fromDate?: string; toDate?: string } = {}): Promise<{ data: TransactionDto[]; total: number; page: number; limit: number; totalPages: number; }> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status) query.set('status', params.status);
    if (params.type) query.set('type', params.type);
    if (params.fromDate) query.set('fromDate', params.fromDate);
    if (params.toDate) query.set('toDate', params.toDate);
    const response = await apiClient.get(`/admin/transactions${query.toString() ? `?${query.toString()}` : ''}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải danh sách giao dịch';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Lấy danh sách giao dịch của người dùng hiện tại (member)
   * @param params - Tham số lọc và phân trang
   * @param params.page - Số trang
   * @param params.limit - Số lượng giao dịch mỗi trang
   * @param params.status - Lọc theo trạng thái
   * @param params.type - Lọc theo loại giao dịch
   * @param params.fromDate - Lọc từ ngày
   * @param params.toDate - Lọc đến ngày
   * @returns Promise chứa danh sách giao dịch của người dùng và thông tin phân trang
   */
  static async listMine(params: { page?: number; limit?: number; status?: string; type?: string; fromDate?: string; toDate?: string } = {}): Promise<{ data: TransactionDto[]; total: number; page: number; limit: number; totalPages: number; }> {
    const query = new URLSearchParams();
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    if (params.status) query.set('status', params.status);
    if (params.type) query.set('type', params.type);
    if (params.fromDate) query.set('fromDate', params.fromDate);
    if (params.toDate) query.set('toDate', params.toDate);
    const response = await apiClient.get(`/transactions${query.toString() ? `?${query.toString()}` : ''}`);
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể tải lịch sử giao dịch';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Xác nhận giao dịch (duyệt giao dịch)
   * @param transactionCode - Mã giao dịch cần xác nhận
   * @returns Promise chứa thông tin giao dịch đã được xác nhận
   */
  static async confirmTransaction(transactionCode: string): Promise<TransactionDto> {
    const response = await fetch(`${this.baseUrl}/transactions/code/${transactionCode}/confirm`, {
      method: 'PATCH',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể duyệt giao dịch';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Từ chối giao dịch
   * @param transactionCode - Mã giao dịch cần từ chối
   * @param reason - Lý do từ chối (tùy chọn)
   * @returns Promise chứa thông tin giao dịch đã bị từ chối
   */
  static async rejectTransaction(transactionCode: string, reason?: string): Promise<TransactionDto> {
    const headers = this.getAuthHeaders();
    const response = await fetch(`${this.baseUrl}/transactions/code/${transactionCode}/fail`, {
      method: 'PATCH',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reason: reason || 'Giao dịch bị từ chối' })
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể từ chối giao dịch';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }

  /**
   * Hủy giao dịch
   * @param transactionCode - Mã giao dịch cần hủy
   * @returns Promise chứa thông tin giao dịch đã bị hủy
   */
  static async cancelTransaction(transactionCode: string): Promise<TransactionDto> {
    const response = await fetch(`${this.baseUrl}/transactions/code/${transactionCode}/cancel`, {
      method: 'PATCH',
      headers: this.getAuthHeaders()
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData?.message || 'Không thể hủy giao dịch';
      throw new Error(Array.isArray(message) ? message.join(', ') : message);
    }
    return response.json();
  }
}


